import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';

import {
  fetchContractsV2Success,
  fetchContractsV2Fail,
  fetchContractStatusSuccess,
  fetchContractStatusFail,
  fetchContractSuccess,
  fetchContractFail,
  fetchContract,
  fetchQuote,
  fetchQuoteSuccess,
  fetchQuoteFail,
  fetchContractEventsSuccess,
  fetchContractEventsFail,
  fetchContractEvents,
  fetchContractDocumentsSuccess,
  fetchContractDocumentsFail,
  fetchContractDocuments,
  fetchContractBeneficiariesSuccess,
  fetchContractBeneficiariesFail,
  fetchContractAttachmentsSuccess,
  fetchContractAttachmentsFail,
  deleteContractDocumentSuccess,
  deleteContractDocumentFail,
  deleteContractAttachmentSuccess,
  deleteContractAttachmentFail,
  addContractDocumentSuccess,
  addContractDocumentFail,
  addContractAttachmentSuccess,
  addContractAttachmentFail,
  submitQuoteSuccess,
  submitQuoteFail,
  cryptologSignSuccess,
  cryptologSignFail,
  cryptologCheckSuccess,
  cryptologCheckFail,
  fetchNotes,
  fetchNotesSuccess,
  fetchNotesFail,
  fetchNoteSuccess,
  fetchNoteFail,
  createNoteSuccess,
  createNoteFail,
  loadMoreContractsSuccess,
  loadMoreContractsFail,
  downloadAttachmentSuccess,
  downloadAttachmentFail,
  updateDocumentRequestLineSuccess,
  updateDocumentRequestLineFail,
} from './actions';
import {
  FETCH_CONTRACTS_REQUEST,
  FETCH_CONTRACT_REQUEST,
  FETCH_QUOTE_REQUEST,
  FETCH_CONTRACT_STATUS_REQUEST,
  FETCH_CONTRACT_EVENTS_REQUEST,
  FETCH_CONTRACT_DOCUMENTS_REQUEST,
  FETCH_CONTRACT_BENEFICIARIES_REQUEST,
  FETCH_CONTRACT_ATTACHMENTS_REQUEST,
  DELETE_CONTRACT_DOCUMENT_REQUEST,
  ADD_CONTRACT_DOCUMENT_REQUEST,
  ADD_CONTRACT_ATTACHMENT_REQUEST,
  DELETE_CONTRACT_ATTACHMENT_REQUEST,
  SUBMIT_QUOTE_REQUEST,
  CRYPTOLOG_CHECK_REQUEST,
  CRYPTOLOG_SIGN_REQUEST,
  FETCH_CONTRACT_NOTE_REQUEST,
  FETCH_CONTRACT_NOTES_REQUEST,
  CREATE_CONTRACT_NOTE_REQUEST,
  LOAD_MORE_CONTRACTS_REQUEST,
  DOWNLOAD_ATTACHMENT_REQUEST,
  UPDATE_DOCUMENT_REQUEST_LINE_REQUEST,
} from './actionTypes';

import { logout } from '../Auth/actions';
import { getOptions, processResponse } from '../../Utils/Utils';

const contractStatusUrl = '/api/v1/contract/model/status';
// CONTRACT EVENTS
const eventsByContractIdUrl = () => '/api/v1/event';
// GET /api/v1/contract/86/document_request_lines?_fields=document_desc
const documentsByContractIdUrl = id => `/api/v1/contract/${id}/document_request_lines`;
// GET /api/v1/ir/attachment?resource=contract,86
const attachmentByContractIdUrl = () => '/api/v1/ir/attachment';
// GET /api/v1/contract/86
// ???
// GET /api/v1/contract/beneficiary?_fields=option%2Cparty&option=%5B339%2C340%2C341%2C342%5D
const beneficiariesByContractIdUrl = () => '/api/v1/contract/beneficiary';
// PUT /api/v1/document/request/line/104 => 104=document_id
// payload {"attachment":16} => 16=attachemnt_id
const updateDocumentByIdUrl = id => `/api/v1/document/request/line/${id}`;
// DELETE /api/v1/ir/attachment/16 16=attachemnt_id
const deleteAttachmentByAttachmentIdUrl = id => `/api/v1/ir/attachment/${id}`;
// POST /api/v1/ir/attachment
// payload {
//   data: "iVBORw0KGgoAAAANSUhEUgAADRoAAAbeCAYAAACLd2hRAAAMTW",
//   name: "Screenshot Quotations 2.png",
//   resource: "contract,91"
// }
const createAttachmentUrl = () => `/api/v1/ir/attachment`;
// POST /api/v1/contract/66/submit
const submitQuoteUrl = id => `/api/v1/contract/${id}/submit`;
// POST /api/v1/ir/attachment/149/cryptolog-launch (SIGNER)
const cryptologSignUrl = id => `/api/v1/ir/attachment/${id}/cryptolog-launch`;
// POST /api/v1/ir/attachment/149/cryptolog-check
const cryptologCheckUrl = id => `/api/v1/ir/attachment/${id}/cryptolog-check`;
// POST /api/v1/ir/note
// {"message":"TEST NOTE 1","resource":"contract,15"}
const createNoteUrl = () => '/api/v1/ir/note';
// GET /api/v1/ir/note?resource=contract,15
const fetchNotesUrl = id => `/api/v1/ir/note?resource=contract,${id}`;
// GET /api/v1/ir/note/3
const fetchNoteUrl = id => `/api/v1/ir/note/${id}`;

const fetchContractsV2 = () => `/api/v2/graphql/b2b`
// GET Download attachment v2
const downloadAttachmentV2 = token => `/api/v2/documents/attachment/${token}`;

// Moved api call into own function (for easy test swapping)
export function fetchFromApi(options) {
  return axios(options)
    .then(response => ({ response }))
    .catch(error => ({ error }));
}

// Small helper: escape a JS string for safe inclusion as a GraphQL string literal
const gqlString = (s) =>
  `"${String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

// Build unpaid-ids-only GraphQL query
const buildUnpaidIdsQuery = ({ networkCode }) => {
  const args = [
    `paginate:false`,
    `isUnpaid: true`,
  ];
  if (networkCode != null) args.push(`networkCode: ${gqlString(networkCode)}`);

  return `
    query {
      contracts(${args.join(", ")}) {
        id
      }
    }
  `;
};

// Fetch full set of unpaid contract ids (handles pagination)
function* fetchUnpaidIdsSet({ networkCode, limit = 500 }) {
  const ids = new Set();

  const q = buildUnpaidIdsQuery({ networkCode });
  const body = JSON.stringify({ query: q, getEntitiesByNetwork: true });

  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: fetchContractsV2(), method: 'POST', data: body })
  );
  const { type, data, alerts } = processResponse({ response, error });

  if (type !== 'success') {
    // surface the error to the caller saga
    return { error: alerts || [{ level: 'error', message: 'Failed to fetch unpaid IDs' }], ids: null };
  }

  const contracts = data?.data?.contracts ?? [];
  contracts.forEach(c => ids.add(String(c.id)));
  return { ids, error: null };
}

// Build the GraphQL query text with INLINED literals (no variables)
const buildContractsQuery = ({ offset, limit, networkCode }) => {
  const args = [
    `offset: ${Number(offset)}`,          // numbers are unquoted
    `limit: ${Number(limit)}`,
  ];

  // only include networkCode if provided; hyphens require quotes → use gqlString
  if (networkCode != null) {
    args.push(`networkCode: ${gqlString(networkCode)}`);
  }

  return `
    query {
      contracts(${args.join(", ")}) {
        id
        contractNumber
        contractStatus
        quoteNumber
        subscriber { name firstName id code }
        createDate
        signatureDate
        startDate
        endDate
        currentTermEndDate
        lastTermEndDate
        product { id name }
        commercialProduct { id name }
      }
    }
  `;
};

// Saga to fetch ALL contracts with pagination, pushing to store every 500 items
function* contracts(action) {
  const limit = action?.payload?.limit ?? 50;
  const networkCode = action?.payload?.networkCode ?? null;

  // 1) Build unpaidIds set first
  const { ids: unpaidIds, error: unpaidErr } = yield call(fetchUnpaidIdsSet, { networkCode, limit: 500 });
  if (unpaidErr) {
    yield put(fetchContractsV2Fail({ error: unpaidErr }));
    return;
  }

  const CHUNK_SIZE = 500;
  let offset = 0;
  let allContracts = [];
  let nextThreshold = CHUNK_SIZE; // prochain palier d'envoi

  try {
    while (true) {
      const graphqlQuery = buildContractsQuery({ offset, limit, networkCode });

      const body = JSON.stringify({
        query: graphqlQuery,
        getEntitiesByNetwork: true,
      });

      const { response, error } = yield call(
        fetchFromApi,
        getOptions({ url: fetchContractsV2(), method: 'POST', data: body })
      );

      const { type, data, alerts } = processResponse({ response, error });

      if (type !== 'success') {
        if (type === 'fail') yield put(fetchContractsV2Fail({ alerts }));
        else if (type === 'logout') yield put(logout());
        return;
      }

      // 2) Enrich page with isUnpaid
      const page = (data?.data?.contracts ?? []).map(c => ({
        ...c,
        isUnpaid: unpaidIds.has(String(c.id)),
        isOutstanding: unpaidIds.has(String(c.id)),
      }));

      allContracts = allContracts.concat(page);

      // push every 500 items (already enriched)
      if (allContracts.length >= nextThreshold) {
        yield put(fetchContractsV2Success({
          data: { contracts: allContracts },
          total: allContracts.length,
          offset: offset + limit,
          partial: false,
        }));
        nextThreshold += CHUNK_SIZE;
      }

      // Fin si la page est incomplète
      if (page.length < limit) break;

      offset += limit;
    }

    // Envoi final (toujours), pour couvrir les cas "0 contrat"
    yield put(fetchContractsV2Success({
      data: { contracts: allContracts },
      total: allContracts.length,
      offset: offset + limit,
      partial: false,
    }));
  } catch (e) {
    yield put(fetchContractsV2Fail({ alerts: [{ level: 'error', message: e.message }] }));
  }
}


function* loadMoreContracts({ offset = 0, limit = 50 }) {
  let graphqlQuery = `query {
    contracts(offset: $offset, limit: $limit, networkCode: "$networkCode"){
      id
      contractNumber
      contractStatus
      quoteNumber
      subscriber {
        name
        id
        code
      }
      createDate
      signatureDate
      startDate
      product {
        id
        name
      }
      commercialProduct {
        id
        name
      }
    }
  }`
  graphqlQuery = graphqlQuery.replace(/\$offset/, offset)

  graphqlQuery = graphqlQuery.replace(/\$limit/g, limit)
  const body = JSON.stringify({
    query: graphqlQuery,
    getEntitiesByNetwork: true
  })
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: fetchContractsV2(), method: 'POST', data: body })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success': {
      const newOffset = offset + limit;
      yield put(loadMoreContractsSuccess({ data: data?.data, offset: newOffset }));
      break;
    }
    case 'fail':
      yield put(loadMoreContractsFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

function* contractBeneficiaries({ id, options = [] }) {
  const params = {
    _fields: 'option,party',
    option: `[${options.toString()}]`,
  };
  const { response, error } = yield call(fetchFromApi, getOptions({ url: beneficiariesByContractIdUrl(id), params }));
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchContractBeneficiariesSuccess({ data, id }));
      break;
    case 'fail':
      yield put(fetchContractBeneficiariesFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

function* contract({ contractNumber }) {
  let graphqlQuery = `query {
    contracts(number: "$contractNumber"){
      id
      contractNumber
      contractStatus
      subscriber {
        name
        id
        code
      }
      coveredElements {
        party {
          id
        }
      }
      createDate
      signatureDate
      startDate
      product {
        id
        name
      }
      documentRequestLines {
        id
        status
        documentDesc {
          name
          code
        }
        attachments {
          token
          metaData {
            name
            date
          }
        }
      }
      currentState
    }
  }`
  graphqlQuery = graphqlQuery.replace(/\$contractNumber/, contractNumber)

  const body = JSON.stringify({
    query: graphqlQuery,
  })

  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: fetchContractsV2(), method: 'POST', data: body })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success': {
      yield put(fetchContractSuccess({ data: data?.data, contractNumber }));
      // const options = data.covered_element_options.map(option => option.id);
      // yield put(fetchContractBeneficiaries({ id, options }));
      break;
    }
    case 'fail':
      yield put(fetchContractFail({ error: alerts, contractNumber }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

function* quote({ quoteNumber }) {
  let graphqlQuery = `query {
    quotes(quoteNumber: "$quoteNumber"){
      id
      contractNumber
      contractStatus
      quoteNumber
      subscriber {
        name
        id
        code
      }
      coveredElements {
        party {
          id
        }
      }
      createDate
      signatureDate
      startDate
      product {
        id
        name
      }
      documentRequestLines {
        id
        status
        documentDesc {
          name
          code
        }
        attachments {
          token
          metaData {
            name
            date
          }
        }
      }
      currentState
    }
  }`
  graphqlQuery = graphqlQuery.replace(/\$quoteNumber/, quoteNumber)

  const body = JSON.stringify({
    query: graphqlQuery,
  })

  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: fetchContractsV2(), method: 'POST', data: body })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success': {
      yield put(fetchQuoteSuccess({ data: data?.data, quoteNumber }));
      // const options = data.covered_element_options.map(option => option.id);
      // yield put(fetchContractBeneficiaries({ id, options }));
      break;
    }
    case 'fail':
      yield put(fetchQuoteFail({ error: alerts, quoteNumber }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

function* contractEvents({ id }) {
  const params = {
    _fields: 'contract,event_type,rec_name,description_str,icon',
    'contract.id': id,
  };
  const { response, error } = yield call(fetchFromApi, getOptions({ url: eventsByContractIdUrl(id), params }));

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchContractEventsSuccess({ data, id }));
      break;
    case 'fail':
      yield put(fetchContractEventsFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

function* contractDocuments({ id }) {
  const params = {
    _fields: 'document_desc,rec_name,received,reception_date,attachment_name,attachment_data',
  };
  const { response, error } = yield call(fetchFromApi, getOptions({ url: documentsByContractIdUrl(id), params }));
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchContractDocumentsSuccess({ data, id }));
      break;
    case 'fail':
      yield put(fetchContractDocumentsFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}

// GET /api/v1/ir/attachment?resource=contract,86
// const attachmentByContractIdUrl = '/api/v1/ir/attachment';
function* contractAttachments({ id }) {
  const params = {
    resource: `contract,${id}`,
  };
  const { response, error } = yield call(fetchFromApi, getOptions({ url: attachmentByContractIdUrl(id), params }));
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchContractAttachmentsSuccess({ data, id }));
      break;
    case 'fail':
      yield put(fetchContractAttachmentsFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}

// ===
function* contractStatus() {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: contractStatusUrl }));
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchContractStatusSuccess({ data }));
      break;
    case 'fail':
      yield put(fetchContractStatusFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}
// const attachmentByContractIdUrl = '/api/v1/ir/attachment';
function* addContractAttachment({ id, name, data }) {
  const payload = {
    data,
    name,
    resource: `contract,${id}`,
  };
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: createAttachmentUrl(), method: 'POST', data: payload })
  );
  const { type, data: dataA, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success': {
      const { id: attachmentId } = dataA;
      yield put(addContractAttachmentSuccess({ attachmentId, id, data: dataA }));
      break;
    }
    case 'fail':
      yield put(addContractAttachmentFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}
// const attachmentByContractIdUrl = '/api/v1/ir/attachment';
function* addContractDocument({ id, documentId, name, data }) {
  const httpResponse = yield* addContractAttachment({ id, name, data });
  const { type: typeA, data: dataA, alerts: alertsA } = processResponse(httpResponse);
  switch (typeA) {
    case 'success': {
      const { id: attachmentId } = dataA;
      const payload = {
        attachment: attachmentId,
      };
      const { response, error } = yield call(
        fetchFromApi,
        getOptions({ url: updateDocumentByIdUrl(documentId), method: 'PUT', data: payload })
      );
      const { type: typeB, data: dataB, alerts: alertsB } = processResponse({ response, error });
      switch (typeB) {
        case 'success': {
          const { res } = dataB;
          yield put(addContractDocumentSuccess({ id, documentId, document: { data: dataA, ...res } }));
          yield put(fetchContract({ id }));
          yield put(fetchContractDocuments({ id }));
          break;
        }
        case 'fail':
          yield put(addContractDocumentFail({ error: alertsB, id: documentId }));
          break;
        case 'logout':
          yield put(logout());
          break;
        default:
      }
      break;
    }
    case 'fail':
      yield put(addContractDocumentFail({ error: alertsA, id: documentId }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return httpResponse;
}

// const deleteAttachmentByAttachmentIdUrl = '/api/v1/ir/attachment';
function* deleteContractAttachment({ attachmentId }) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: deleteAttachmentByAttachmentIdUrl(attachmentId), method: 'DELETE' })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(deleteContractAttachmentSuccess({ id: attachmentId, data }));
      break;
    case 'fail':
      yield put(deleteContractAttachmentFail({ error: alerts, id: attachmentId }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}

// const attachmentByContractIdUrl = '/api/v1/ir/attachment';
function* deleteContractDocument({ id, documentId, attachmentId }) {
  const httpResponse = yield call(
    fetchFromApi,
    getOptions({ url: updateDocumentByIdUrl(documentId), method: 'PUT', data: { attachment: 0 } })
  );
  const { type: typeA, data: dataA, alerts: alertsA } = processResponse(httpResponse);
  switch (typeA) {
    case 'success': {
      const { response, error } = yield* deleteContractAttachment({ dataA, id, attachmentId });
      const { type: typeB, data: dataB, alerts: alertsB } = processResponse({ response, error });
      switch (typeB) {
        case 'success': {
          yield put(deleteContractDocumentSuccess({ data: { ...dataA, ...dataB }, id, attachmentId }));
          yield put(fetchContract({ id }));
          yield put(fetchContractDocuments({ id }));
          break;
        }
        case 'fail':
          yield put(deleteContractDocumentFail({ error: alertsB, id: attachmentId }));
          break;
        case 'logout':
          yield put(logout());
          break;
        default:
      }
      break;
    }
    case 'fail':
      yield put(deleteContractDocumentFail({ error: alertsA, id: attachmentId }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return httpResponse;
}

// POST /api/v1/contract/66/submit
function* submitQuote({ id }) {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: submitQuoteUrl(id), method: 'POST' }));
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(submitQuoteSuccess({ data: data.res, id }));
      yield put(fetchContract({ id }));
      yield put(fetchContractEvents({ id }));
      break;
    case 'fail':
      yield put(submitQuoteFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}

// POST /api/v1/ir/attachment/149/cryptolog-launch (SIGN)
function* cryptologSign({ id }) {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: cryptologSignUrl(id), method: 'POST' }));

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(cryptologSignSuccess({ data: data.res, id }));
      yield put({ type: FETCH_CONTRACT_REQUEST, id });
      yield put({ type: FETCH_CONTRACT_EVENTS_REQUEST, id });
      break;
    case 'fail':
      yield put(cryptologSignFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}
// POST /api/v1/ir/attachment/149/cryptolog-launch (CHECK)
function* cryptologCheck({ id }) {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: cryptologCheckUrl(id), method: 'POST' }));

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(cryptologCheckSuccess({ data: data.res, id }));
      yield put({ type: FETCH_CONTRACT_REQUEST, id });
      yield put({ type: FETCH_CONTRACT_EVENTS_REQUEST, id });
      break;
    case 'fail':
      yield put(cryptologCheckFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}

// GET /api/v1/ir/note?resource=contract,15
function* notes({ id }) {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: fetchNotesUrl(id), method: 'GET' }));

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchNotesSuccess({ data, id }));
      break;
    case 'fail':
      yield put(fetchNotesFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}

// GET /api/v1/ir/note/3
function* note({ id }) {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: fetchNoteUrl(id), method: 'GET' }));

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchNoteSuccess({ data, id }));
      break;
    case 'fail':
      yield put(fetchNoteFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}

// POST /api/v1/ir/note
// {"message":"TEST NOTE 1","resource":"contract,15"}
function* createNote({ id, message }) {
  const payload = {
    message,
    resource: `contract,${id}`,
  };
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: createNoteUrl(), method: 'POST', data: payload })
  );

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(createNoteSuccess({ data, id }));
      yield put(fetchNotes({ id }));
      break;
    case 'fail':
      yield put(createNoteFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
  }
  return { response, error };
}

function* downloadAttachment({ token }) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: downloadAttachmentV2(token), method: 'GET', responseType: 'blob' })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success': {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split('filename=')[1].replace(/"/g, '');

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      yield put(downloadAttachmentSuccess({ data: data?.data }));
      break;
    }
    case 'fail':
      yield put(downloadAttachmentFail({ alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}


export function* updateDocumentRequestLine({ query, files, contractNumber, quoteNumber }) {

  const formData = new FormData();
  formData.append('query', JSON.stringify(query));
  files.forEach((file, index) => {
    const blob = new Blob([file.buffer], { type: file.mimetype }); // Convert Buffer to Blob
    formData.append('data', blob, file.originalname || file.name); // Append Blob to FormData
  });

  const options = getOptions({
    url: '/api/v2/document-request-lines/actions/update',
    method: 'PUT',
    data: formData,
    extraHeaders: { 'Content-Type': 'multipart/form-data' },
  });

  const { response, error } = yield call(fetchFromApi, options);

  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(updateDocumentRequestLineSuccess({ data }));
      if (quoteNumber) {
        yield put(fetchQuote({ quoteNumber }));
      } else if (contractNumber) {
        yield put(fetchContract({ contractNumber }));
      }
      break;
    case 'fail':
      yield put(updateDocumentRequestLineFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

function* watchContracts() {
  yield takeLatest(FETCH_CONTRACTS_REQUEST, contracts);
  yield takeLatest(FETCH_CONTRACT_REQUEST, contract);
  yield takeLatest(FETCH_QUOTE_REQUEST, quote);
  yield takeLatest(FETCH_CONTRACT_STATUS_REQUEST, contractStatus);
  yield takeLatest(FETCH_CONTRACT_EVENTS_REQUEST, contractEvents);
  yield takeLatest(FETCH_CONTRACT_DOCUMENTS_REQUEST, contractDocuments);
  yield takeLatest(FETCH_CONTRACT_BENEFICIARIES_REQUEST, contractBeneficiaries);
  yield takeLatest(FETCH_CONTRACT_ATTACHMENTS_REQUEST, contractAttachments);
  yield takeLatest(DELETE_CONTRACT_ATTACHMENT_REQUEST, deleteContractAttachment);
  yield takeLatest(DELETE_CONTRACT_DOCUMENT_REQUEST, deleteContractDocument);
  yield takeLatest(ADD_CONTRACT_DOCUMENT_REQUEST, addContractDocument);
  yield takeLatest(ADD_CONTRACT_ATTACHMENT_REQUEST, addContractAttachment);
  yield takeLatest(SUBMIT_QUOTE_REQUEST, submitQuote);
  yield takeLatest(CRYPTOLOG_SIGN_REQUEST, cryptologSign);
  yield takeLatest(CRYPTOLOG_CHECK_REQUEST, cryptologCheck);
  yield takeLatest(FETCH_CONTRACT_NOTES_REQUEST, notes);
  yield takeLatest(FETCH_CONTRACT_NOTE_REQUEST, note);
  yield takeLatest(CREATE_CONTRACT_NOTE_REQUEST, createNote);
  yield takeLatest(LOAD_MORE_CONTRACTS_REQUEST, loadMoreContracts);
  yield takeLatest(DOWNLOAD_ATTACHMENT_REQUEST, downloadAttachment);
  yield takeLatest(UPDATE_DOCUMENT_REQUEST_LINE_REQUEST, updateDocumentRequestLine);
}

export default watchContracts;
